package ru.it_mozaika.sender.js.application.frames.files

import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.utils.io.js.*
import js.core.JsLong
import js.core.asList
import js.core.jso
import kotlinx.coroutines.*
import mui.icons.material.CloudUploadSharp
import mui.icons.material.DeleteSharp
import mui.icons.material.FilePresentSharp
import mui.material.*
import mui.system.responsive
import mui.system.sx
import oldcomponents.NotyType
import oldcomponents.showNotification
import org.khronos.webgl.ArrayBuffer
import react.*
import react.dom.events.ChangeEvent
import react.dom.events.MouseEvent
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.input
import ru.it_mozaika.components.AvatarButton
import ru.it_mozaika.sender.common.httpClient.client
import ru.it_mozaika.sender.common.models.S3File
import ru.it_mozaika.sender.common.models.availableExtensions
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.sender.js.application.context.BackdropContext
import ru.it_mozaika.sender.js.application.frames.MainCard
import ru.it_mozaika.utils.checkResponseError
import tanstack.react.table.renderCell
import tanstack.react.table.useReactTable
import tanstack.table.core.*
import web.cssom.px
import web.data.DataTransfer
import web.file.File
import web.file.FileList
import web.html.ButtonType
import web.html.HTMLButtonElement
import web.html.HTMLInputElement
import web.html.InputType

private enum class Columns(val ruTitle: String) {
    ID("ID"), Title("Наименование"), Date("Дата создания")
}

val s3ListFrame = VFC {
    val rowsPerPage by useState(10)
    val page by useState(0)
    val searchQuery by useState<String>("")
    val (rows, setRows) = useState(listOf<S3File>())
    val backdrop = useContext(BackdropContext)
    val (selectedFiles, setSelectedFiles) = useState<FileList>()
    val fileInputRef: MutableRefObject<HTMLInputElement> = useRef<HTMLInputElement>(null)

    fun requestDataList() {
        val query: MutableMap<String, String> = mutableMapOf(
            "offset" to (page * rowsPerPage).toString(),
            "limit" to rowsPerPage.toString(),
        )
        if (searchQuery.isNotBlank()) {
            query["search"] = searchQuery
        }
        backdrop.show()
        Http.get("/files", query) { httpResponse: HttpResponse ->
            backdrop.hide()
            httpResponse.checkResponseError()?.let { error ->
                showNotification(error, NotyType.Error)
                return@get
            }
            val responseData: List<S3File> = httpResponse.body<List<S3File>>()
            setRows(responseData)
        }
    }

    val handleAddFile: (ChangeEvent<HTMLInputElement>) -> Unit = { event: ChangeEvent<HTMLInputElement> ->
        val files: FileList? = event.target.files
        setSelectedFiles { currentFiles ->
            val dataTransfer: DataTransfer = DataTransfer()
            val oldFilesLength: Int? = files?.length
            if (oldFilesLength != null) {
                for (i in 0 until oldFilesLength) {
                    val oldFile: File = files[i]
                    dataTransfer.items.add(oldFile)
                }
            }
            val newFilesLength: Int? = currentFiles?.length
            if (newFilesLength != null) {
                for (i in 0 until newFilesLength) {
                    val newFile: File = currentFiles[i]
                    if (!dataTransfer.files.asList().contains(newFile)) {
                        dataTransfer.items.add(newFile)
                    }
                }
            }
            dataTransfer.files
        }
    }

    val handleButtonClick: (MouseEvent<HTMLButtonElement, *>) -> Unit = {
        fileInputRef.current?.click()
    }

    val sendFiles: (fileList: FileList) -> Job = { fileList: FileList ->
        CoroutineScope(Dispatchers.Main).launch {
            val data: List<FileDto> = prepareFileData(fileList)
            val formData = formData {
                for (fileDto: FileDto in data) {
                    val filenameWithExtension: String = fileDto.filenameWithExtension
                    val dotIndex: Int = filenameWithExtension.lastIndexOf('.')

                    if (dotIndex == -1) continue

                    val filename: String = filenameWithExtension.substring(0, dotIndex)

                    val contentType: ContentType = ContentType.parse(fileDto.contentTypeString)

                    val size: JsLong = fileDto.size
                    val webglArrayBuffer: ArrayBuffer = fileDto.webglArrayBuffer

                    append(filename, filenameWithExtension, contentType, size.toLong()) {
                        writeFully(webglArrayBuffer, 0, size.toInt())
                    }
                }
            }
            client.post("/api/v1/files") {
                setBody(MultiPartFormDataContent(formData))
            }
        }
    }

    val handleSubmit: (MouseEvent<HTMLButtonElement, *>) -> Unit = { event: MouseEvent<HTMLButtonElement, *> ->
        event.preventDefault()
        selectedFiles?.let {
            sendFiles(it).invokeOnCompletion {
                requestDataList()
            }
        }
    }

    val handleDeleteFile: (File) -> Unit = { fileToDelete: File ->
        setSelectedFiles { currentFiles: FileList? ->
            val dataTransfer: DataTransfer = DataTransfer()
            if (currentFiles == null) {
                dataTransfer.items.clear()
            } else {
                currentFiles.asList().filter { it != fileToDelete }.forEach {
                    dataTransfer.items.add(it)
                }
            }
            dataTransfer.files
        }
    }

    val deleteById: (id: String) -> Unit = { id: String ->
        Http.delete("/files/$id", emptyMap()) { httpResponse: HttpResponse ->
            httpResponse.checkResponseError()?.let { error ->
                showNotification(error, NotyType.Error)
            }
        }
    }

    useEffect(rowsPerPage, searchQuery, page) {
        requestDataList()
    }

    val table: Table<S3File> = useReactTable<S3File>(
        options = jso {
            columns = Columns.entries.map { column: Columns ->
                jso<ColumnDef<S3File, Any>> {
                    accessorFn = { s3File: S3File, _: Any ->
                        when (column) {
                            Columns.ID -> s3File.id
                            Columns.Title -> s3File.filename + '.' + s3File.filenameExtension
                            Columns.Date -> s3File.creationDate.date
                        }
                    }
                    cell = ColumnDefTemplate { context ->
                        val value = context.getValue().toString()
                        if (context.column.id == Columns.Title.name) {
                            Link.create {
                                href = "../file/$value"
                                +value
                            }
                        } else {
                            Box.create {
                                +value
                            }
                        }
                    }
                    id = column.name
                    header = StringOrTemplateHeader(column.ruTitle)
                }
            }.toTypedArray().plus(
                jso<ColumnDef<S3File, Any>> {
                    header = StringOrTemplateHeader("Удалить")
                    cell = ColumnDefTemplate { context ->
                        val id: String = context.row.getValue(Columns.ID.name).toString()
                        AvatarButton.create {
                            tooltipTitle = "Удаление"
                            DeleteSharp {
                                color = SvgIconColor.error
                            }
                            onClick = {
                                deleteById(id)
                                setRows(rows.filter { it.id != id })
                            }
                        }

                    }
                    id = "Delete"
                })
            data = useMemo(rows) { rows.toTypedArray() }
            getCoreRowModel = getCoreRowModel()
            debugTable = true
            debugHeaders = true
            debugColumns = true
        })

    MainCard {
        title = "Файлы"
        form {
            input {
                type = InputType.file
                ref = fileInputRef
                onChange = handleAddFile
                multiple = true
                hidden = true
                accept = availableExtensions.joinToString()
            }

            AvatarButton {
                variant = ButtonVariant.contained
                tooltipTitle = "Выбрать файл"
                sx { margin = 8.px }
                onClick = handleButtonClick
                FilePresentSharp()
            }

            AvatarButton {
                type = ButtonType.submit
                tooltipTitle = "Загрузить файл"
                sx { margin = 8.px }
                onClick = handleSubmit
                CloudUploadSharp()
            }

            Stack {
                direction = responsive(StackDirection.row)
                sx { marginTop = 2.px }
                selectedFiles?.asList()?.forEach { file ->
                    Chip {
                        key = file.name
                        label = ReactNode(file.name)
                        onDelete = {
                            handleDeleteFile(file)
                        }
                        variant = ChipVariant.outlined
                        sx { margin = 0.5.px }
                    }
                }
            }
        }

        Box {
            TableContainer {
                component = Paper
                Table {
                    TableHead {
                        TableRow {
                            table.getHeaderGroups().forEach { headerGroup ->
                                headerGroup.headers.forEach { header ->
                                    TableCell { +header.column.columnDef.header.toString() }
                                }
                            }
                        }
                    }
                    TableBody {
                        table.getRowModel().rows.forEach { row ->
                            TableRow {
                                row.getVisibleCells().forEach { cell ->
                                    TableCell { +renderCell(cell) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private suspend fun prepareFileData(fileList: FileList): List<FileDto> {
    val result: MutableList<FileDto> = mutableListOf<FileDto>()
    for (i in 0 until fileList.length) {
        val file: File = fileList.item(i) ?: continue
        val contentTypeString: String = file.type
        val filenameWithExtension: String = file.name
        val size: JsLong = file.size
        val arrayBuffer: js.buffer.ArrayBuffer = file.arrayBuffer().await()
        val webglArrayBuffer: ArrayBuffer = arrayBuffer.unsafeCast<ArrayBuffer>()
        result.add(FileDto(filenameWithExtension, size, webglArrayBuffer, contentTypeString))
    }
    return result
}
